import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  FiCreditCard,
  FiMail,
  FiLock,
  FiAlertCircle,
  FiShield,
  FiCopy,
  FiCheck,
  FiArrowLeft,
} from "react-icons/fi";

// Get auth frontend URL for forgot password and register links
const getAuthFrontendUrl = () =>
  window.ENV?.REACT_APP_AUTH_FRONTEND_URL ||
  process.env.REACT_APP_AUTH_FRONTEND_URL ||
  "https://auth.beilke.com.br";

const getAppId = () =>
  window.ENV?.REACT_APP_APP_ID || process.env.REACT_APP_APP_ID || "payments";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // MFA states
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [mfaSetupToken, setMfaSetupToken] = useState(null);
  const [mfaCode, setMfaCode] = useState("");

  // MFA Setup states
  const [mfaStep, setMfaStep] = useState(1); // 1: QR, 2: Verify, 3: Backup codes
  const [mfaSecret, setMfaSecret] = useState("");
  const [mfaQrCode, setMfaQrCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [setupCode, setSetupCode] = useState("");
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedBackupCodes, setCopiedBackupCodes] = useState(false);

  // Health check state
  const [healthStatus, setHealthStatus] = useState({
    healthy: false,
    version: null,
    checking: true,
  });

  const { login, verifyMfa, getAuthConfig } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/dashboard";

  // Check auth service health status
  useEffect(() => {
    const checkHealth = async () => {
      try {
        const { url, apiKey } = getAuthConfig();
        const response = await fetch(`${url}/health`, {
          method: "GET",
          headers: {
            "X-API-Key": apiKey || "",
          },
        });
        if (response.ok) {
          const data = await response.json();
          setHealthStatus({
            healthy: data.status === "healthy",
            version: data.version,
            checking: false,
          });
        } else {
          setHealthStatus({ healthy: false, version: null, checking: false });
        }
      } catch (error) {
        console.error("Health check failed:", error);
        setHealthStatus({ healthy: false, version: null, checking: false });
      }
    };

    checkHealth();
    // Check health every 30 seconds
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, [getAuthConfig]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const data = await login({ email, password });

      // Check for MFA requirements
      if (data.mfaRequired) {
        setMfaRequired(true);
        setTempToken(data.tempToken);
        setLoading(false);
        return;
      }

      if (data.mfaSetupRequired) {
        setMfaSetupRequired(true);
        setMfaSetupToken(data.mfaSetupToken);
        setLoading(false);
        // Start MFA setup
        await initiateMfaSetup(data.mfaSetupToken);
        return;
      }

      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || "Failed to login");
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Pass email and password along with MFA code
      await verifyMfa(email, password, mfaCode);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || "MFA verification failed");
    } finally {
      setLoading(false);
    }
  };

  // MFA Setup Functions
  const initiateMfaSetup = async (token) => {
    setLoading(true);
    setError("");
    try {
      const { url, apiKey } = getAuthConfig();
      const response = await fetch(`${url}/mfa/setup-with-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-API-Key": apiKey,
        },
        body: JSON.stringify({ mfaSetupToken: token }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to start MFA setup");
      }

      const data = await response.json();
      setMfaSecret(data.secret);
      setMfaQrCode(data.qrCode);
      setMfaStep(1);
    } catch (err) {
      console.error("MFA setup error:", err);
      setError(err.message || "Failed to initiate MFA setup");
    } finally {
      setLoading(false);
    }
  };

  const verifyMfaSetup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { url, apiKey } = getAuthConfig();
      const response = await fetch(`${url}/mfa/verify-setup-with-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${mfaSetupToken}`,
          "X-API-Key": apiKey,
        },
        body: JSON.stringify({
          mfaSetupToken: mfaSetupToken,
          code: setupCode,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Invalid verification code");
      }

      const data = await response.json();
      setBackupCodes(data.backupCodes || []);
      setMfaStep(3);
    } catch (err) {
      setError(err.message || "Failed to verify MFA setup");
    } finally {
      setLoading(false);
    }
  };

  const completeMfaSetup = async () => {
    // After viewing backup codes, user needs to login again with MFA
    setMfaSetupRequired(false);
    setMfaSetupToken(null);
    setMfaStep(1);
    setMfaSecret("");
    setMfaQrCode("");
    setBackupCodes([]);
    setSetupCode("");
    setError("");
    // Show success message
    setError("MFA setup complete! Please login again with your MFA code.");
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === "secret") {
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    } else {
      setCopiedBackupCodes(true);
      setTimeout(() => setCopiedBackupCodes(false), 2000);
    }
  };

  const resetToLogin = () => {
    setMfaRequired(false);
    setMfaSetupRequired(false);
    setTempToken(null);
    setMfaSetupToken(null);
    setMfaCode("");
    setMfaStep(1);
    setMfaSecret("");
    setMfaQrCode("");
    setBackupCodes([]);
    setSetupCode("");
    setError("");
  };

  // Render MFA Setup wizard
  const renderMfaSetup = () => {
    return (
      <div className="card" style={{ maxWidth: "450px", width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <FiShield size={48} color="var(--primary)" />
          <h1 style={{ marginTop: "16px", fontSize: "24px" }}>
            Set Up Two-Factor Authentication
          </h1>
          <p className="text-muted">
            This application requires MFA. Please set it up to continue.
          </p>
        </div>

        {error && (
          <div
            className="alert alert-error"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "16px",
            }}
          >
            <FiAlertCircle />
            {error}
          </div>
        )}

        {/* Step indicators */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "8px",
            marginBottom: "24px",
          }}
        >
          {[1, 2, 3].map((step) => (
            <div
              key={step}
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor:
                  mfaStep >= step ? "var(--primary)" : "var(--bg-secondary)",
                color: mfaStep >= step ? "white" : "var(--text-secondary)",
                fontWeight: "bold",
                fontSize: "14px",
              }}
            >
              {step}
            </div>
          ))}
        </div>

        {/* Step 1: QR Code */}
        {mfaStep === 1 && (
          <div>
            <p style={{ marginBottom: "16px", color: "#333" }}>
              Scan this QR code with your authenticator app (Google
              Authenticator, Authy, etc.):
            </p>

            {mfaQrCode && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: "16px",
                }}
              >
                <img
                  src={mfaQrCode}
                  alt="MFA QR Code"
                  style={{
                    maxWidth: "200px",
                    border: "1px solid var(--border-color)",
                    borderRadius: "8px",
                  }}
                />
              </div>
            )}

            <div
              style={{
                backgroundColor: "#f5f5f5",
                padding: "12px",
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            >
              <p
                style={{
                  fontSize: "12px",
                  color: "#333",
                  marginBottom: "8px",
                }}
              >
                Can't scan? Enter this code manually:
              </p>
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: "14px",
                  color: "#333",
                  wordBreak: "break-all",
                  marginBottom: "8px",
                }}
              >
                {mfaSecret}
              </div>
              <button
                type="button"
                onClick={() => copyToClipboard(mfaSecret, "secret")}
                className="btn btn-secondary"
                style={{ fontSize: "12px", padding: "4px 8px" }}
              >
                {copiedSecret ? (
                  <>
                    <FiCheck /> Copied
                  </>
                ) : (
                  <>
                    <FiCopy /> Copy
                  </>
                )}
              </button>
            </div>

            <button
              type="button"
              onClick={() => setMfaStep(2)}
              className="btn btn-primary"
              style={{ width: "100%" }}
            >
              Continue
            </button>
          </div>
        )}

        {/* Step 2: Verify Code */}
        {mfaStep === 2 && (
          <form onSubmit={verifyMfaSetup}>
            <p style={{ marginBottom: "16px", color: "#333" }}>
              Enter the 6-digit code from your authenticator app:
            </p>

            <div className="form-group">
              <input
                type="text"
                className="form-input"
                placeholder="000000"
                value={setupCode}
                onChange={(e) =>
                  setSetupCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                style={{
                  textAlign: "center",
                  fontSize: "24px",
                  letterSpacing: "8px",
                }}
                maxLength={6}
                autoFocus
              />
            </div>

            <div style={{ display: "flex", gap: "8px" }}>
              <button
                type="button"
                onClick={() => setMfaStep(1)}
                className="btn btn-secondary"
                style={{ flex: 1 }}
              >
                <FiArrowLeft /> Back
              </button>
              <button
                type="submit"
                disabled={loading || setupCode.length !== 6}
                className="btn btn-primary"
                style={{ flex: 2 }}
              >
                {loading ? "Verifying..." : "Verify & Enable MFA"}
              </button>
            </div>
          </form>
        )}

        {/* Step 3: Backup Codes */}
        {mfaStep === 3 && (
          <div>
            <div
              className="alert alert-success"
              style={{ marginBottom: "16px" }}
            >
              <FiCheck style={{ marginRight: "8px" }} />
              MFA has been enabled successfully!
            </div>

            <p style={{ marginBottom: "16px", color: "#333" }}>
              <strong>Important:</strong> Save these backup codes in a safe
              place. You can use them to access your account if you lose your
              authenticator device.
            </p>

            <div
              style={{
                backgroundColor: "#f5f5f5",
                padding: "16px",
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "8px",
                  fontFamily: "monospace",
                  fontSize: "14px",
                  color: "#333",
                }}
              >
                {backupCodes.map((code, index) => (
                  <div key={index}>{code}</div>
                ))}
              </div>
              <button
                type="button"
                onClick={() =>
                  copyToClipboard(backupCodes.join("\n"), "backup")
                }
                className="btn btn-secondary"
                style={{
                  marginTop: "12px",
                  fontSize: "12px",
                  padding: "4px 8px",
                }}
              >
                {copiedBackupCodes ? (
                  <>
                    <FiCheck /> Copied
                  </>
                ) : (
                  <>
                    <FiCopy /> Copy All
                  </>
                )}
              </button>
            </div>

            <button
              type="button"
              onClick={completeMfaSetup}
              className="btn btn-primary"
              style={{ width: "100%" }}
            >
              I've Saved My Codes - Continue to Login
            </button>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "16px" }}>
          <button
            type="button"
            onClick={resetToLogin}
            style={{
              background: "none",
              border: "none",
              color: "var(--text-secondary)",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            <FiArrowLeft style={{ marginRight: "4px" }} />
            Back to Login
          </button>
        </div>
      </div>
    );
  };

  // Render MFA Verification
  const renderMfaVerify = () => {
    return (
      <div className="card" style={{ maxWidth: "400px", width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <FiShield size={48} color="var(--primary)" />
          <h1 style={{ marginTop: "16px", fontSize: "24px" }}>
            Two-Factor Authentication
          </h1>
          <p className="text-muted">
            Enter the code from your authenticator app
          </p>
        </div>

        {error && (
          <div
            className="alert alert-error"
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <FiAlertCircle />
            {error}
          </div>
        )}

        <form onSubmit={handleMfaVerify}>
          <div className="form-group">
            <label className="form-label">Authentication Code</label>
            <input
              type="text"
              className="form-input"
              placeholder="000000"
              value={mfaCode}
              onChange={(e) =>
                setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              style={{
                textAlign: "center",
                fontSize: "24px",
                letterSpacing: "8px",
              }}
              maxLength={6}
              autoFocus
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: "100%", marginTop: "16px" }}
            disabled={loading || mfaCode.length !== 6}
          >
            {loading ? "Verifying..." : "Verify"}
          </button>
        </form>

        <div style={{ textAlign: "center", marginTop: "16px" }}>
          <button
            type="button"
            onClick={resetToLogin}
            style={{
              background: "none",
              border: "none",
              color: "var(--text-secondary)",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            <FiArrowLeft style={{ marginRight: "4px" }} />
            Back to Login
          </button>
        </div>
      </div>
    );
  };

  // Render Login Form
  const renderLoginForm = () => {
    const authFrontendUrl = getAuthFrontendUrl();
    const appId = getAppId();
    const returnUrl = encodeURIComponent(window.location.origin + "/login");

    return (
      <div className="card" style={{ maxWidth: "400px", width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <FiCreditCard size={48} color="var(--primary)" />
          <h1 style={{ marginTop: "16px", fontSize: "24px" }}>
            Payments Dashboard
          </h1>
          <p className="text-muted">Sign in to manage your payments</p>
        </div>

        {error && (
          <div
            className="alert alert-error"
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <FiAlertCircle />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <div style={{ position: "relative" }}>
              <FiMail
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--text-secondary)",
                }}
              />
              <input
                type="email"
                className="form-input"
                style={{ paddingLeft: "40px" }}
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <div style={{ position: "relative" }}>
              <FiLock
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--text-secondary)",
                }}
              />
              <input
                type="password"
                className="form-input"
                style={{ paddingLeft: "40px" }}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div style={{ textAlign: "right", marginTop: "8px" }}>
              <a
                href={`${authFrontendUrl}/forgot-password?application=${appId}&returnUrl=${returnUrl}`}
                style={{ color: "var(--primary)", fontSize: "14px" }}
              >
                Forgot password?
              </a>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: "100%", marginTop: "16px" }}
            disabled={loading}
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <p
          style={{ textAlign: "center", marginTop: "24px", fontSize: "14px" }}
          className="text-muted"
        >
          Don't have an account?{" "}
          <a
            href={`${authFrontendUrl}/register?application=${appId}&returnUrl=${returnUrl}`}
            style={{ color: "var(--primary)" }}
          >
            Create one
          </a>
        </p>

        {/* Auth Service Health Status */}
        <div
          style={{
            textAlign: "center",
            marginTop: "24px",
            paddingTop: "16px",
            borderTop: "1px solid var(--border-color)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: healthStatus.checking
                  ? "#ffc107"
                  : healthStatus.healthy
                  ? "#28a745"
                  : "#dc3545",
                display: "inline-block",
              }}
            ></span>
            <small className="text-muted">
              Auth Service:{" "}
              {healthStatus.checking
                ? "Checking..."
                : healthStatus.healthy
                ? "Online"
                : "Offline"}
            </small>
          </div>
          {healthStatus.version && (
            <small className="text-muted" style={{ fontSize: "11px" }}>
              v{healthStatus.version}
            </small>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      {mfaSetupRequired
        ? renderMfaSetup()
        : mfaRequired
        ? renderMfaVerify()
        : renderLoginForm()}
    </div>
  );
}

export default Login;
